import React, { useState, useRef, useEffect } from 'react';
import { Signal, SignalLow, SignalMedium, WifiOff, RefreshCw, Clock, Activity, ChevronDown } from 'lucide-react';
import { useNetwork } from '../../contexts/NetworkContext';

interface NetworkStatusIndicatorProps {
  compact?: boolean;
  showLabel?: boolean;
}

export const NetworkStatusIndicator: React.FC<NetworkStatusIndicatorProps> = ({
  compact = false,
  showLabel = true,
}) => {
  const { state, history, pendingOperationsCount, checkConnection } = useNetwork();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleCheckConnection = async () => {
    setIsChecking(true);
    await checkConnection();
    setIsChecking(false);
  };

  const getStatusConfig = () => {
    if (!state.isOnline || state.quality === 'offline') {
      return {
        icon: WifiOff,
        label: 'Offline',
        color: 'text-red-500',
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        pulse: true,
      };
    }

    switch (state.quality) {
      case 'excellent':
        return {
          icon: Signal,
          label: 'Excellent',
          color: 'text-green-500',
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          pulse: false,
        };
      case 'good':
        return {
          icon: SignalMedium,
          label: 'Good',
          color: 'text-green-500',
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          pulse: false,
        };
      case 'slow':
        return {
          icon: SignalLow,
          label: 'Slow',
          color: 'text-amber-500',
          bg: 'bg-amber-50 dark:bg-amber-900/20',
          border: 'border-amber-200 dark:border-amber-800',
          pulse: false,
        };
      default:
        return {
          icon: Signal,
          label: 'Connected',
          color: 'text-green-500',
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          pulse: false,
        };
    }
  };

  const config = getStatusConfig();
  const IconComponent = config.icon;

  const formatTime = (date: Date | null) => {
    if (!date) return 'N/A';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatLatency = (latency: number | null) => {
    if (latency === null) return 'N/A';
    return `${latency}ms`;
  };

  if (compact) {
    return (
      <div className="relative" title={config.label}>
        <div className={`relative ${config.color}`}>
          <IconComponent className="h-4 w-4" />
          {config.pulse && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg} border ${config.border} transition-colors hover:opacity-80`}
      >
        <div className="relative">
          <IconComponent className={`h-4 w-4 ${config.color}`} />
          {config.pulse && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          )}
        </div>
        {showLabel && (
          <span className={`text-xs font-medium ${config.color.replace('text-', 'text-').replace('-500', '-600')} dark:${config.color.replace('text-', 'text-').replace('-500', '-400')}`}>
            {config.label}
          </span>
        )}
        {pendingOperationsCount > 0 && (
          <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-amber-500 rounded-full">
            {pendingOperationsCount}
          </span>
        )}
        <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Connection Status
                </span>
              </div>
              <button
                onClick={handleCheckConnection}
                disabled={isChecking}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Check connection"
              >
                <RefreshCw className={`h-4 w-4 text-gray-500 ${isChecking ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${config.bg}`}>
                <IconComponent className={`h-3.5 w-3.5 ${config.color}`} />
                <span className={`text-xs font-medium ${config.color.replace('-500', '-700')} dark:${config.color.replace('-500', '-300')}`}>
                  {config.label}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Latency</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatLatency(state.latency)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Last Check</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatTime(state.lastChecked)}
              </span>
            </div>

            {pendingOperationsCount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Pending Sync</span>
                <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                  {pendingOperationsCount} operations
                </span>
              </div>
            )}
          </div>

          {history.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700">
              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Recent History
                </span>
              </div>
              <div className="max-h-32 overflow-y-auto">
                {history.slice(-5).reverse().map((entry, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 flex items-center justify-between text-xs border-b border-gray-100 dark:border-gray-700/50 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${entry.state === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-gray-600 dark:text-gray-300 capitalize">{entry.state}</span>
                    </div>
                    <span className="text-gray-400">
                      {formatTime(entry.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
